import FilterDate from '@/Enums/FilterDate.enum';
import { ScheduleInterval } from '@/Enums/ReportBuilder/ScheduleInterval.enum';
import { ISchedulePdfReportInitialValues } from '@/Interfaces/PdfBuilder/ScheduledPdfReportInitialValues.interface';
import { IScheduleFormData } from '@/Interfaces/ReportBuilder/ScheduleFormData.interface';
import CalendarIcon from '@/components/Icons/CalendarIcon';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import ModalBody from '@/components/Modal/common/ModalBody';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import SelectionChip from '@/components/Select/SelectionChips/SelectionChip';
import { DATE_RANGE_FORMAT_LABELS } from '@/constants/date';
import { emailRegex, emailSeparatorRegex, spaceOrCommaSeparatedEmail } from '@/constants/regex/email';
import { INTERVAL_LABELS } from '@/constants/reportBuilder';
import TitleIcon from '@/pages/ReportBuilder/Components/ScheduleReportModal/TitleIcon';
import { Button, InputLabel, MenuItem, Modal, Select, TextField, selectClasses } from '@mui/material';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { ClipboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

interface IProps {
  open: boolean;
  dateRangeFormat: IScheduleFormData['dateRangeFormat'];
  mode?: 'create' | 'edit';
  initialValues?: ISchedulePdfReportInitialValues;
  onClose: () => void;
  onSubmit: (scheduleFormData: IScheduleFormData) => Promise<void>;
}

const DESIRED_DATE_RANGE_VALUES = Object.values(FilterDate).filter(fd => fd !== FilterDate.CUSTOM_RANGE);

const ScheduleReportModal = ({ open, dateRangeFormat, mode = 'create', initialValues, onClose, onSubmit }: IProps) => {
  const [loading, setLoading] = useState(false);

  const defaultValues = useMemo<IScheduleFormData>(() => {
    if (mode === 'edit' && initialValues) {
      return {
        reportName: initialValues.reportName,
        interval: initialValues.interval,
        recipientEmail: '',
        recipientEmails: initialValues.recipientEmails,
        startDate: initialValues.startDate,
        dateRangeFormat: initialValues.dateRangeFormat,
      };
    }
    return {
      reportName: '',
      interval: ScheduleInterval.Daily,
      recipientEmail: '',
      recipientEmails: [] as string[],
      startDate: moment().add({ day: 1 }),
      dateRangeFormat,
    };
  }, [mode, initialValues, dateRangeFormat]);

  const formMethods = useForm<IScheduleFormData>({ defaultValues });

  const { reset, handleSubmit, watch, getValues, setValue, control } = formMethods;

  useEffect(() => {
    if (open) reset(defaultValues);
  }, [open, defaultValues, reset]);

  const recipientEmails = useWatch({ name: 'recipientEmails', control });

  const handleValidEmails = useCallback(
    (validEmails: string[] = []) => {
      const recipientEmailsSet = new Set(getValues('recipientEmails'));

      validEmails.forEach(email => {
        recipientEmailsSet.add(email);
      });
      setValue('recipientEmails', Array.from(recipientEmailsSet.values()), { shouldDirty: true });
      setTimeout(() => setValue('recipientEmail', ''), 0);
    },
    [getValues, setValue],
  );

  useEffect(() => {
    if (!watch) return;
    const subscription = watch((value, { name, type }) => {
      if (
        name === 'recipientEmail' &&
        type === 'change' &&
        spaceOrCommaSeparatedEmail.test(value.recipientEmail ?? '')
      ) {
        handleValidEmails([value.recipientEmail!.replace(emailSeparatorRegex, '')]);
      }
    });
    return () => subscription.unsubscribe();
  }, [handleValidEmails, watch]);

  useEffect(() => {
    if (mode === 'create') {
      setValue('dateRangeFormat', dateRangeFormat);
    }
  }, [dateRangeFormat, mode, setValue]);

  const onModalClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const onRemoveEmail = useCallback(
    (email: string) => () =>
      setValue(
        'recipientEmails',
        getValues('recipientEmails').filter(record => record !== email),
      ),
    [getValues, setValue],
  );

  const _onSubmit = useCallback(
    async (data: IScheduleFormData) => {
      const pending = data.recipientEmail?.trim();
      const recipientEmails =
        pending && emailRegex.test(pending) && !data.recipientEmails.includes(pending)
          ? [...data.recipientEmails, pending]
          : data.recipientEmails;

      setLoading(true);
      try {
        await onSubmit({ ...data, recipientEmail: '', recipientEmails });
        setLoading(false);

        onModalClose();
        toast.success(mode === 'edit' ? 'Scheduled report updated' : 'Report scheduled successfully');
      } catch (error) {
        console.error(error);
        setLoading(false);
        toast.error('Something went wrong');
      }
    },
    [mode, onModalClose, onSubmit],
  );

  const onEmailsPaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      const value = event.clipboardData?.getData('text').trim();
      const emails = value.split(emailSeparatorRegex);

      const validEmails = emails.filter(email => emailRegex.test(email ?? ''));

      if (!validEmails.length) {
        return;
      }

      event.preventDefault();

      handleValidEmails(validEmails);
    },
    [handleValidEmails],
  );

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Modal open={open} onClose={onModalClose}>
        <ModalBody>
          <ModalTitleCard
            titleIcon={<TitleIcon />}
            handleClose={onModalClose}
            title={mode === 'edit' ? 'Edit Scheduled Report' : 'Schedule Report'}
            sx={{
              marginBottom: '16px',
            }}
          />
          <FormProvider {...formMethods}>
            <form className='mt-4' onSubmit={handleSubmit(_onSubmit)}>
              <InputLabel sx={{ marginBottom: '4px' }} htmlFor='pdf-schedule-report-name'>
                Report Name
              </InputLabel>
              <Controller
                name='reportName'
                control={control}
                rules={{
                  required: {
                    message: 'Report Name is required',
                    value: true,
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    placeholder='Enter Report Name'
                    id='pdf-schedule-report-name'
                    sx={{ marginBottom: '8px' }}
                    slotProps={{
                      htmlInput: { maxLength: 250 },
                    }}
                  />
                )}
              />
              <InputLabel sx={{ marginBottom: '4px' }} htmlFor='pdf-schedule-date-range-select'>
                Desired Date Range
              </InputLabel>
              <Controller
                name='dateRangeFormat'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    fullWidth
                    id='pdf-schedule-date-range-select'
                    sx={{
                      marginBottom: '8px',
                      [`& .${selectClasses.select}`]: { paddingLeft: '8px' },
                    }}
                    startAdornment={<CalendarIcon />}
                    renderValue={(value: FilterDate) => <>{DATE_RANGE_FORMAT_LABELS[value]}</>}
                  >
                    {DESIRED_DATE_RANGE_VALUES.map(value => (
                      <MenuItem key={value} value={value}>
                        {DATE_RANGE_FORMAT_LABELS[value]}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <InputLabel sx={{ marginBottom: '4px' }} htmlFor='pdf-schedule-interval-select'>
                Interval
              </InputLabel>
              <Controller
                name='interval'
                control={control}
                rules={{
                  required: { message: 'Interval is required', value: true },
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    fullWidth
                    id='pdf-schedule-interval-select'
                    sx={{
                      marginBottom: '8px',
                      [`& .${selectClasses.select}`]: { paddingLeft: '8px' },
                    }}
                    startAdornment={<CalendarIcon />}
                    renderValue={(value: ScheduleInterval) => <>{INTERVAL_LABELS[value]}</>}
                  >
                    {Object.values(ScheduleInterval).map(value => (
                      <MenuItem key={value} value={value}>
                        {INTERVAL_LABELS[value]}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <InputLabel sx={{ marginBottom: '4px' }} htmlFor='pdf-schedule-delivery-date-time'>
                Delivery start date and time
              </InputLabel>
              <Controller
                name='startDate'
                control={control}
                rules={{
                  required: { message: 'Delivery start date & time is required.', value: true },
                }}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MobileDateTimePicker
                      {...field}
                      orientation='landscape'
                      disablePast
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          id: 'pdf-schedule-delivery-date-time',
                          sx: { marginBottom: '8px' },
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
              <InputLabel sx={{ marginBottom: '4px' }} htmlFor='pdf-schedule-recipient-email'>
                Recipient Emails
              </InputLabel>
              <Controller
                name='recipientEmail'
                control={control}
                rules={{
                  validate: (value, formValues) => {
                    const trimmed = value?.trim();
                    const hasPending = !!trimmed && emailRegex.test(trimmed);
                    if (!formValues.recipientEmails.length && !hasPending) {
                      return 'Please enter at least one valid email.';
                    }
                    if (trimmed && !hasPending) {
                      return 'Please enter a valid email.';
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!error}
                    autoComplete='email'
                    helperText={error?.message}
                    placeholder='Enter Emails'
                    id='pdf-schedule-recipient-email'
                    sx={{ marginBottom: '8px' }}
                    onPaste={onEmailsPaste}
                  />
                )}
              />
              <div className='flex flex-wrap gap-2'>
                {recipientEmails?.map(email => (
                  <SelectionChip key={email} label={email} onRemove={onRemoveEmail(email)} />
                ))}
              </div>
              <hr className='-mx-[32px] my-5' />
              <div className='mt-5 flex justify-end gap-2'>
                <Button variant='outlined' onClick={onModalClose}>
                  Cancel
                </Button>
                <Button variant='contained' type='submit'>
                  {mode === 'edit' ? 'Save' : 'Confirm'}
                </Button>
              </div>
            </form>
          </FormProvider>
        </ModalBody>
      </Modal>
    </LoadingBackdropWrapper>
  );
};

export default ScheduleReportModal;
